import { jsx, Fragment } from "react/jsx-runtime";
import * as React from "react";
import * as prismicR from "@prismicio/richtext";
import { PrismicLink } from "./PrismicLink.js";
import { devMsg } from "../lib/devMsg.js";
import { richText } from './../_node_modules/@prismicio/client/dist/helpers/isFilled.js';
const createDefaultSerializer = args => prismicR.wrapMapSerializer({
  heading1: _ref => {
    let {
      children,
      key
    } = _ref;
    return /* @__PURE__ */jsx("h1", {
      children
    }, key);
  },
  heading2: _ref2 => {
    let {
      children,
      key
    } = _ref2;
    return /* @__PURE__ */jsx("h2", {
      children
    }, key);
  },
  heading3: _ref3 => {
    let {
      children,
      key
    } = _ref3;
    return /* @__PURE__ */jsx("h3", {
      children
    }, key);
  },
  heading4: _ref4 => {
    let {
      children,
      key
    } = _ref4;
    return /* @__PURE__ */jsx("h4", {
      children
    }, key);
  },
  heading5: _ref5 => {
    let {
      children,
      key
    } = _ref5;
    return /* @__PURE__ */jsx("h5", {
      children
    }, key);
  },
  heading6: _ref6 => {
    let {
      children,
      key
    } = _ref6;
    return /* @__PURE__ */jsx("h6", {
      children
    }, key);
  },
  paragraph: _ref7 => {
    let {
      children,
      key
    } = _ref7;
    return /* @__PURE__ */jsx("p", {
      children
    }, key);
  },
  preformatted: _ref8 => {
    let {
      node,
      key
    } = _ref8;
    return /* @__PURE__ */jsx("pre", {
      children: node.text
    }, key);
  },
  strong: _ref9 => {
    let {
      children,
      key
    } = _ref9;
    return /* @__PURE__ */jsx("strong", {
      children
    }, key);
  },
  em: _ref10 => {
    let {
      children,
      key
    } = _ref10;
    return /* @__PURE__ */jsx("em", {
      children
    }, key);
  },
  listItem: _ref11 => {
    let {
      children,
      key
    } = _ref11;
    return /* @__PURE__ */jsx("li", {
      children
    }, key);
  },
  oListItem: _ref12 => {
    let {
      children,
      key
    } = _ref12;
    return /* @__PURE__ */jsx("li", {
      children
    }, key);
  },
  list: _ref13 => {
    let {
      children,
      key
    } = _ref13;
    return /* @__PURE__ */jsx("ul", {
      children
    }, key);
  },
  oList: _ref14 => {
    let {
      children,
      key
    } = _ref14;
    return /* @__PURE__ */jsx("ol", {
      children
    }, key);
  },
  image: _ref15 => {
    var _node$alt;
    let {
      node,
      key
    } = _ref15;
    const img = /* @__PURE__ */jsx("img", {
      src: node.url,
      alt: (_node$alt = node.alt) !== null && _node$alt !== void 0 ? _node$alt : void 0,
      "data-copyright": node.copyright ? node.copyright : void 0
    });
    return /* @__PURE__ */jsx("p", {
      className: "block-img",
      children: node.linkTo ? /* @__PURE__ */jsx(PrismicLink, {
        linkResolver: args.linkResolver,
        internalComponent: args.internalLinkComponent,
        externalComponent: args.externalLinkComponent,
        field: node.linkTo,
        children: img
      }) : img
    }, key);
  },
  embed: _ref16 => {
    var _node$oembed$html;
    let {
      node,
      key
    } = _ref16;
    return /* @__PURE__ */jsx("div", {
      "data-oembed": node.oembed.embed_url,
      "data-oembed-type": node.oembed.type,
      "data-oembed-provider": node.oembed.provider_name,
      dangerouslySetInnerHTML: {
        __html: (_node$oembed$html = node.oembed.html) !== null && _node$oembed$html !== void 0 ? _node$oembed$html : ""
      }
    }, key);
  },
  hyperlink: _ref17 => {
    let {
      node,
      children,
      key
    } = _ref17;
    return /* @__PURE__ */jsx(PrismicLink, {
      field: node.data,
      linkResolver: args.linkResolver,
      internalComponent: args.internalLinkComponent,
      externalComponent: args.externalLinkComponent,
      children
    }, key);
  },
  label: _ref18 => {
    let {
      node,
      children,
      key
    } = _ref18;
    return /* @__PURE__ */jsx("span", {
      className: node.data.label,
      children
    }, key);
  },
  span: _ref19 => {
    let {
      text,
      key
    } = _ref19;
    const result = [];
    let i = 0;
    for (const line of text.split("\n")) {
      if (i > 0) {
        result.push( /* @__PURE__ */jsx("br", {}, "".concat(i, "__break")));
      }
      result.push( /* @__PURE__ */jsx(React.Fragment, {
        children: line
      }, "".concat(i, "__line")));
      i++;
    }
    return /* @__PURE__ */jsx(React.Fragment, {
      children: result
    }, key);
  }
});
function PrismicRichText(_ref20) {
  let {
    linkResolver,
    field,
    fallback,
    components,
    externalLinkComponent,
    internalLinkComponent,
    ...restProps
  } = _ref20;
  return React.useMemo(() => {
    if (typeof process !== "undefined" && process.env.NODE_ENV === "development") {
      if ("className" in restProps) {
        console.warn("[PrismicRichText] className cannot be passed to <PrismicRichText> since it renders an array without a wrapping component. For more details, see ".concat(devMsg("classname-is-not-a-valid-prop"), "."), field);
      }
    }
    if (richText(field)) {
      const serializer = prismicR.composeSerializers(typeof components === "object" ? prismicR.wrapMapSerializer(components) : components, createDefaultSerializer({
        linkResolver,
        internalLinkComponent,
        externalLinkComponent
      }));
      const serialized = prismicR.serialize(field, (type, node, text, children, key) => {
        const result = serializer(type, node, text, children, key);
        if (React.isValidElement(result) && result.key == null) {
          return React.cloneElement(result, {
            key
          });
        } else {
          return result;
        }
      });
      return /* @__PURE__ */jsx(Fragment, {
        children: serialized
      });
    } else {
      return fallback != null ? /* @__PURE__ */jsx(Fragment, {
        children: fallback
      }) : null;
    }
  }, [field, internalLinkComponent, externalLinkComponent, components, linkResolver, fallback]);
}
export { PrismicRichText };